import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, Plus, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { deleteVoiceVocabulary, getVoiceVocabulary } from '../api';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const CATEGORIES = [
  ['geography_market', 'Market / Geography'],
  ['buyer', 'Buyer'],
  ['supplier', 'Supplier'],
  ['employee', 'Employee'],
  ['trade_item', 'Trade Item'],
  ['textile_term', 'Textile Term'],
];

const emptyManualMapping = { variant: '', preferred_term: '', category: 'textile_term' };

export default function VoiceVocabularyDialog({
  open,
  onOpenChange,
  suggestions = [],
  reviewMode = false,
  saving = false,
  onConfirm,
  onSkip,
}) {
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [manualMapping, setManualMapping] = useState(emptyManualMapping);
  const [manualMappings, setManualMappings] = useState([]);
  const [existingMappings, setExistingMappings] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedSuggestions(suggestions.map((_, index) => index));
    setManualMappings([]);
    setManualMapping(emptyManualMapping);
    setLoadingExisting(true);
    getVoiceVocabulary()
      .then((response) => setExistingMappings(response.data || []))
      .catch(() => setExistingMappings([]))
      .finally(() => setLoadingExisting(false));
  }, [open, suggestions]);

  const confirmedMappings = useMemo(() => [
    ...selectedSuggestions.map((index) => ({ ...suggestions[index], source: 'suggested' })),
    ...manualMappings.map((mapping) => ({ ...mapping, source: 'manual' })),
  ], [manualMappings, selectedSuggestions, suggestions]);

  const addManualMapping = () => {
    const variant = manualMapping.variant.trim().replace(/\s+/g, ' ');
    const preferred = manualMapping.preferred_term.trim().replace(/\s+/g, ' ');
    if (!variant || !preferred) {
      toast.error('Enter both the heard term and preferred VOHK term');
      return;
    }
    if (variant.toLowerCase() === preferred.toLowerCase()) {
      toast.error('The heard and preferred terms must be different');
      return;
    }
    setManualMappings((current) => [
      ...current,
      { ...manualMapping, variant, preferred_term: preferred },
    ]);
    setManualMapping(emptyManualMapping);
  };

  const removeExisting = async (mappingId) => {
    try {
      await deleteVoiceVocabulary(mappingId);
      setExistingMappings((current) => current.filter((mapping) => mapping.id !== mappingId));
      toast.success('Vocabulary mapping removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove mapping');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="voice-vocabulary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-maroon-950">
            <BookOpen className="h-5 w-5 text-maroon-700" />
            VOHK Voice Vocabulary
          </DialogTitle>
          <DialogDescription>
            Remember only specific names or business terms—not full sentence rewrites.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {suggestions.length > 0 && (
            <section className="space-y-2" data-testid="vocabulary-suggestions-section">
              <div className="flex items-center gap-2 text-sm font-semibold text-maroon-900">
                <Sparkles className="h-4 w-4" /> Suggested from your edits
              </div>
              {suggestions.map((suggestion, index) => (
                <label key={`${suggestion.variant}-${index}`} className="flex items-start gap-3 border border-maroon-200 p-3 rounded-md cursor-pointer">
                  <Checkbox
                    checked={selectedSuggestions.includes(index)}
                    onCheckedChange={(checked) => setSelectedSuggestions((current) => (
                      checked ? [...current, index] : current.filter((item) => item !== index)
                    ))}
                    data-testid={`vocabulary-suggestion-${index}`}
                  />
                  <span className="min-w-0 text-sm">
                    <span className="line-through text-gray-500">{suggestion.variant}</span>
                    <span className="mx-2 text-gray-400">→</span>
                    <strong className="text-maroon-900">{suggestion.preferred_term}</strong>
                    <span className="block mt-1 text-xs text-gray-500">
                      {CATEGORIES.find(([value]) => value === suggestion.category)?.[1] || suggestion.category}
                    </span>
                  </span>
                </label>
              ))}
            </section>
          )}

          <section className="space-y-3 border-t border-maroon-100 pt-4">
            <div className="text-sm font-semibold text-maroon-900">Add a mapping manually</div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 items-end">
              <div>
                <Label htmlFor="voice-variant" className="text-xs">Heard / wrong term</Label>
                <Input
                  id="voice-variant"
                  value={manualMapping.variant}
                  onChange={(event) => setManualMapping((current) => ({ ...current, variant: event.target.value }))}
                  placeholder="Kalva Devi"
                  data-testid="vocabulary-variant-input"
                />
              </div>
              <span className="hidden sm:block pb-2 text-gray-400">→</span>
              <div>
                <Label htmlFor="voice-preferred" className="text-xs">Preferred VOHK term</Label>
                <Input
                  id="voice-preferred"
                  value={manualMapping.preferred_term}
                  onChange={(event) => setManualMapping((current) => ({ ...current, preferred_term: event.target.value }))}
                  placeholder="Kalbadevi"
                  data-testid="vocabulary-preferred-input"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Select
                value={manualMapping.category}
                onValueChange={(category) => setManualMapping((current) => ({ ...current, category }))}
              >
                <SelectTrigger className="sm:w-56" data-testid="vocabulary-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {CATEGORIES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" onClick={addManualMapping} data-testid="add-manual-vocabulary-button">
                <Plus className="h-4 w-4 mr-1" /> Add mapping
              </Button>
            </div>
            {manualMappings.map((mapping, index) => (
              <div key={`${mapping.variant}-${index}`} className="flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 p-2 rounded-md text-sm">
                <span><span className="text-gray-500">{mapping.variant}</span> → <strong>{mapping.preferred_term}</strong></span>
                <Button type="button" variant="ghost" size="sm" onClick={() => setManualMappings((current) => current.filter((_, itemIndex) => itemIndex !== index))} data-testid={`remove-pending-vocabulary-${index}`}>
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            ))}
          </section>

          <section className="space-y-2 border-t border-maroon-100 pt-4">
            <div className="text-sm font-semibold text-maroon-900">Current memory</div>
            {loadingExisting ? (
              <p className="text-xs text-gray-500" data-testid="vocabulary-memory-loading">Loading…</p>
            ) : existingMappings.length === 0 ? (
              <p className="text-xs text-gray-500" data-testid="vocabulary-memory-empty">No saved mappings yet.</p>
            ) : (
              <div className="space-y-1 max-h-36 overflow-y-auto" data-testid="vocabulary-memory-list">
                {existingMappings.map((mapping) => (
                  <div key={mapping.id} className="flex items-center justify-between gap-2 px-2 py-1.5 bg-gray-50 rounded text-xs">
                    <span className="truncate">{mapping.variant} → <strong>{mapping.preferred_term}</strong></span>
                    <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeExisting(mapping.id)} data-testid={`delete-vocabulary-${mapping.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <DialogFooter className="gap-2">
          {reviewMode ? (
            <Button type="button" variant="outline" onClick={onSkip} disabled={saving} data-testid="skip-vocabulary-button">
              Skip and save visit
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="close-vocabulary-button">
              Close
            </Button>
          )}
          <Button
            type="button"
            onClick={() => onConfirm(confirmedMappings)}
            disabled={saving || (!reviewMode && confirmedMappings.length === 0)}
            className="bg-maroon-700 hover:bg-maroon-800"
            data-testid="confirm-vocabulary-button"
          >
            {saving ? 'Saving…' : reviewMode ? `Remember ${confirmedMappings.length} and save` : `Remember ${confirmedMappings.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}